import _defineProperty from "@babel/runtime/helpers/defineProperty";
/*
 *                     NOTICE
 *            COPYRIGHT 2019 Ab Initio
 *       UNPUBLISHED - ALL RIGHTS RESERVED
 *
 * This program is confidential and a trade secret of Ab Initio.
 * The receipt or possession of this program does not convey any
 * rights to reproduce or disclose its contents, or to manufacture,
 * use or sell anything that it may describe, in whole or in part,
 * without the specific written consent of Ab Initio.
 */

import { AbWebServices } from "abweb/service/AbWebServices";
import { ClassUtils } from "abweb/utils/ClassUtils";
import { IllegalStateError } from "abweb/utils/error/IllegalStateError";
import { PromiseUtils } from "abweb/utils/PromiseUtils";
export class KeyboardService {
  constructor() {
    _defineProperty(this, "_keyDownEventListeners", void 0);
    _defineProperty(this, "_keyUpEventListeners", void 0);
    this._keyDownEventListeners = new Map();
    this._keyUpEventListeners = new Map();
  }
  async onKeyPressHandleShortcuts(e, handlers, label) {
    if (KeyboardService.EXCLUSIONS.indexOf(e.key) !== -1) {
      return false;
    }
    if (ClassUtils.isInstanceOf(e.target, HTMLTextAreaElement) || ClassUtils.isInstanceOf(e.target, HTMLInputElement)) {
      return false;
    }

    // EI-15943 Algorithm to avoid RPC's for keypresses that don't match anything:
    //  1. Gather known IKeyboardShortcutHandlers that match the particular keypress, without caring whether
    //      or not they'll be enabled.
    //  2. If there are none, be done
    //  3. For those that are found, wait until they are initialized, letting them to RPC's to check permissions
    //      and so on
    //  4. Filter down to those that have a callback and are enabled
    //  5. If there are more than one, throw an error
    //  6. If there's only 1, check for a callback, enabled, etc.

    // Find all the handlers that have a callback (so, no separators) and match the keypress
    const matchHandlers = handlers.filter(handler => {
      return handler.callback && handler.shortcut?.matches(e);
      return false;
    });

    // no matches. done
    if (matchHandlers.length === 0) {
      return false;
    }

    // Initialize each matching handler. This allows RPC's to check permissions and so on
    await PromiseUtils.forEach(matchHandlers.map(handler => () => {
      return handler.initializeAndWait();
    }));

    // Look for matches that have a callback and are enabled. We're filtering for handlers that are enabled
    const enabledHandlers = matchHandlers.filter(handler => handler.enabled);

    // 1 match. Perfect! Call its callback.
    if (enabledHandlers.length === 1) {
      const handler = enabledHandlers[0];
      handler.callback(e.target);
      return true;
    }

    // More than 1 match. Bad menu! Throw an exception.
    // Perhaps some day we'll do something else, like pop a menu listing the matches.
    if (enabledHandlers.length > 1) {
      throw new IllegalStateError(`${enabledHandlers.length} handlers have shortcut ${enabledHandlers[0].shortcut.toString()}`);
    }

    // No matches. Not handled.
    return false;
  }
  getShortcuts(target) {
    return [];
  }
  registerForKeyDown(target, handlers, label) {
    const existingEventListener = this._keyDownEventListeners.get(target);
    if (existingEventListener != null) {
      target.removeEventListener("keydown", existingEventListener);
    }
    const eventListener = e => AbWebServices.keyboardService.onKeyPressHandleShortcuts(e, handlers, label);
    this._keyDownEventListeners.set(target, eventListener);
    target.addEventListener("keydown", eventListener);
  }
  registerForKeyUp(target, handlers, label) {
    const existingEventListener = this._keyUpEventListeners.get(target);
    if (existingEventListener != null) {
      target.removeEventListener("keyup", existingEventListener);
    }
    const eventListener = e => AbWebServices.keyboardService.onKeyPressHandleShortcuts(e, handlers, label);
    this._keyUpEventListeners.set(target, eventListener);
    target.addEventListener("keyup", eventListener);
  }
  unregisterShortcuts(target) {
    let existingEventListener = this._keyDownEventListeners.get(target);
    if (existingEventListener != null) {
      target.removeEventListener("keydown", existingEventListener);
    }
    existingEventListener = this._keyUpEventListeners.get(target);
    if (existingEventListener != null) {
      target.removeEventListener("keyup", existingEventListener);
    }
  }
}
/**
 * The <code>KeyboardEvent.key</code> strings to not consider performing actions for. This is primarily an
 * optimization based on the fact that (a) when you press (for example) Control+S, we get two events - one for
 * Control and one for S, and (b) we aren't a video game, and so Control by itself will never be an actual
 * shortcut key. By ignoring these key events, we can process more efficiently. This is especially important in
 * cases where getting the actions fires off an RPC.
 *
 * @type {string[]}
 */
_defineProperty(KeyboardService, "EXCLUSIONS", [
// "Control",
"Alt", "Shift", "Meta"]);
//# sourceMappingURL=KeyboardService.js.map
